// ═══════════════════════════════════════════════════════════════════════
// data/aiCache.js — ai_analysis_cache table access (extracted from App.js)
// ═══════════════════════════════════════════════════════════════════════
// Read/write helpers for the AI-insight cache. Behaviour-identical to the
// previous inline queries (same filters, same conditional channel_id match).
// RLS is the guard. The caller still computes the fingerprint + time window.
import { supabase } from "../lib/supabaseClient";

// Active report language ("ar" when the operator picked Arabic, else "en").
// Mixed into the fingerprints below so an Arabic report and an English report
// for the SAME data live in separate cache buckets — otherwise a cached
// English analysis would be served to an Arabic user (and vice-versa).
// try/catch: localStorage can throw in private-mode / SSR; default to English.
function reportLang() {
  try {
    if (typeof window !== "undefined" && window.localStorage &&
        window.localStorage.getItem("netrev_lang") === "ar") {
      return "ar";
    }
  } catch (e) { /* localStorage unavailable */ }
  return "en";
}

// Most-recent cached analysis matching the fingerprint within the time window.
// chId null/undefined => match the "all channels" rows (channel_id IS NULL).
export function getCachedAnalysis({ wsId, periodMode, fingerprint, chId, sinceISO }) {
  let q = supabase
    .from("ai_analysis_cache")
    .select("analysis_text,generated_at")
    .eq("workspace_id", wsId)
    .eq("period_mode", periodMode)
    .eq("data_fingerprint", fingerprint)
    .gte("generated_at", sinceISO)
    .order("generated_at", { ascending: false })
    .limit(1);
  q = chId ? q.eq("channel_id", chId) : q.is("channel_id", null);
  return q.maybeSingle();
}

export function saveAnalysisToCache(payload) {
  return supabase.from("ai_analysis_cache").insert(payload);
}

// Fingerprint for the Price Tool AI report. Keyed on the menu items + the rates
// that move per-item economics (commission/card fee/VAT) + currency, so editing
// any price, cost, discount, fee or rate invalidates the cached pricing report.
// Reuses the same ai_analysis_cache table via period_mode = "pricetool".
export function computeMenuFingerprint({ items, vatRate, cogsPct, currency, channelRates }) {
  const sortedItems = [...(items || [])].sort((a, b) =>
    String(a.id || a.name || '').localeCompare(String(b.id || b.name || ''))
  );
  const sortedChannels = [...(channelRates || [])].sort((a, b) =>
    String(a.id || '').localeCompare(String(b.id || ''))
  );
  const stable = {
    // Bump when the pricing-report PROMPT changes, to invalidate old caches.
    pv: "pt-v1-suggested-pricing",
    // Report language — Arabic vs English cache in separate buckets.
    lang: reportLang(),
    cur: currency || 'GBP',
    v: Number(vatRate || 0).toFixed(4),
    c: Number(cogsPct || 0).toFixed(4),
    ch: sortedChannels.map(c =>
      `${c.id}:${Number(c.commission || 0).toFixed(4)}:${Number(c.cardFeePct || 0).toFixed(4)}`
    ).join(','),
    n: sortedItems.length,
    it: sortedItems.map(m => [
      m.id || '',
      m.name || '',
      Number(m.cost || 0).toFixed(2),
      Number(m.price || 0).toFixed(2),
      Number(m.disc || 0).toFixed(4),
      Number(m.fee || 0).toFixed(2),
      m.channelId || '',
    ].join('|')),
  };
  const str = JSON.stringify(stable);
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h + str.charCodeAt(i)) | 0;
  return Math.abs(h).toString(36) + '_' + str.length.toString(36);
}

export function computeDataFingerprint({rows, vatRate, cogsPct, fixedMonthly, periodMode, channelRates, cpcTotal, coFundTotal}) {
  const sortedRows = [...rows].sort((a,b) =>
    (String(a.date||'') + String(a.channelId||'')).localeCompare(String(b.date||'') + String(b.channelId||''))
  );
  // Sort channel rates for deterministic ordering
  const sortedChannels = [...(channelRates || [])].sort((a,b) =>
    String(a.id||'').localeCompare(String(b.id||''))
  );
  const stable = {
    // Bump this when the AI prompt changes, so a new prompt invalidates old
    // cached analyses (otherwise users see stale, shorter analyses).
    pv: "v9-cofund",
    // Report language — Arabic vs English cache in separate buckets.
    lang: reportLang(),
    p: periodMode,
    v: Number(vatRate || 0).toFixed(4),
    c: Number(cogsPct || 0).toFixed(4),
    f: Number(fixedMonthly || 0).toFixed(2),
    cpc: Number(cpcTotal || 0).toFixed(2), // full period CPC from allocations (incl. days with no sales entry)
    cf: Number(coFundTotal || 0).toFixed(2), // platform co-funding total (lib/coFund.js)
    // Channel rates (commission + card fee) — change here invalidates cache
    ch: sortedChannels.map(c =>
      `${c.id}:${Number(c.commission || 0).toFixed(4)}:${Number(c.cardFeePct || 0).toFixed(4)}`
    ).join(','),
    n: sortedRows.length,
    // Per-row data
    r: sortedRows.map(r => [
      r.date,
      r.channelId || '',
      Number(r.gross||0).toFixed(2),
      Number(r.net||0).toFixed(2),
      Number(r.cpc||0).toFixed(2),
      Number(r.campaignFee||0).toFixed(2),
      Number(r.coFund||0).toFixed(2),
      // Per-row rate OVERRIDES (null = uses channel default which is in 'ch' above)
      r.commission != null ? Number(r.commission).toFixed(4) : 'null',
      r.cardFeePct != null ? Number(r.cardFeePct).toFixed(4) : 'null',
    ].join('|')),
  };
  const str = JSON.stringify(stable);
  // djb2 string hash
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h + str.charCodeAt(i)) | 0;
  return Math.abs(h).toString(36) + '_' + str.length.toString(36);
}
